import React from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { DropdownSelect } from '../../../../../components/common/DropdownSelect/DropdownSelect'
import { societyQueries } from '../../../../../api/societies'
import { ISocietiesModel } from '../../../../../interfaces/models/societies.interfaces'
import { ITicketBoard } from '../../../../../interfaces/models/ticket.interfaces'
import { IDropdownSelectOption } from '../../../../../components/common/DropdownSelect/IDropdownSelectOption'
import { TicketAvatar } from '../../../../../components/common/Avatar'
import {
  infiniteTicketQueries,
  setAssignedUser,
  ticketQueries,
} from '../../../../../api/ticket'
import { DropdownItemContent } from '../../../../../components/feed/DropdownItemContent/DropdownItemContent'
import { FormControl } from '../../../../../components/common/FormControl'
import { useAppTranslation } from '../../../../../hooks/useAppTranslation'
import { getUserFullName } from '../../../../../helpers/user'
import { ToastType } from '../../../../../components/common/Toast/toast-type'
import { useToastNotifications } from '../../../../../hooks/useToastNotification'

const boardAndAdminIds = (society?: ISocietiesModel): string[] => {
  const uniqueIds = new Set<string>()
  const masterId = society?.masterAdminUserId
  if (masterId) {
    uniqueIds.add(masterId)
  }
  society?.adminsList?.forEach((admin) => uniqueIds.add(admin.userId))
  society?.boardMembersList?.forEach((board) => uniqueIds.add(board.userId))
  return [...uniqueIds]
}
export const AssignUserToTicket = ({
  society,
  ticket,
}: {
  society: ISocietiesModel
  ticket: ITicketBoard
}): React.ReactNode => {
  const { translate } = useAppTranslation()
  const { setToastNotification } = useToastNotifications()
  const { data: societyUsers } = useQuery(societyQueries.getSocietyUsers())

  const boardAndAdmins = societyUsers?.filter((user) =>
    boardAndAdminIds(society).includes(user._id)
  )

  const dropdownOptions: IDropdownSelectOption[] =
    boardAndAdmins?.map((user) => ({
      value: user._id,
      label: getUserFullName(user),
      image: <TicketAvatar size={30} user={user} url={user.avatarUrl} />,
    })) || []

  const queryClient = useQueryClient()

  const { mutate, isPending } = useMutation({
    mutationFn: async ({ assignedUserId }: { assignedUserId: string }) => {
      const response = await setAssignedUser(
        { societyId: society._id, ticketId: ticket.id },
        {
          assignedUserId,
        }
      )
      return response.data.data
    },
    onSuccess: async (updatedTicket) => {
      queryClient.setQueryData(
        ticketQueries.getTicketAsBoard(ticket.id, society._id).queryKey,
        updatedTicket
      )
      await queryClient.invalidateQueries({
        queryKey: infiniteTicketQueries.ticketsBoard({
          societyId: society._id,
        }).queryKey,
      })
    },
    onError: () => {
      setToastNotification(
        ToastType.DANGER,
        translate('flashMessage.somethingWentWrongError')
      )
    },
  })

  return (
    <div className="mb-2">
      <FormControl
        label={translate('ticketBoardSidebar.assignLabel')}
        name="SelectAssigne"
      >
        <DropdownSelect
          value={ticket.assignedUser?.id || ''}
          isLoading={isPending}
          onChange={(selectedId) => {
            if (selectedId === (ticket.assignedUser?.id || '')) {
              return
            }
            mutate({ assignedUserId: selectedId })
          }}
          options={[
            { value: '', label: translate('ticketBoardSidebar.unassigned') },
            ...dropdownOptions,
          ]}
          dropdownWidth="w-full"
          renderItemContent={(option) => (
            <DropdownItemContent option={option} />
          )}
        />
      </FormControl>
    </div>
  )
}
